import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { auth } from '../lib/firebase';
import { ClientProfile, AreaClienteStats } from '../types/areaCliente';
import { areaClienteService } from '../services/areaClienteService';

interface AreaClienteContextType {
  clienteAtual: ClientProfile | null;
  setClienteAtual: (cliente: ClientProfile | null) => void;
  listaClientes: ClientProfile[];
  stats: AreaClienteStats | null;
  isLoading: boolean;
  isClientLocked: boolean;
  /** true quando o usuário logado não tem nenhum cliente vinculado e não é um e-mail interno/admin */
  semVinculo: boolean;
  refreshData: () => Promise<void>;
  selecionarClientePorId: (id: number) => void;
}

const AreaClienteContext = createContext<AreaClienteContextType | undefined>(undefined);

export function AreaClienteProvider({ children }: { children: ReactNode }) {
  const [clienteAtual, setClienteAtual] = useState<ClientProfile | null>(null);
  const [listaClientes, setListaClientes] = useState<ClientProfile[]>([]);
  const [stats, setStats] = useState<AreaClienteStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isClientLocked, setIsClientLocked] = useState<boolean>(false);
  const [semVinculo, setSemVinculo] = useState<boolean>(false);

  // Carrega a lista inicial de clientes e detecta se o usuário logado possui vínculo direto com um cliente
  const initializeClientPortal = useCallback(async () => {
    setIsLoading(true);
    try {
      const clientes = await areaClienteService.getClientes();
      setListaClientes(clientes);

      // Verificar usuário logado atual no Firebase Auth
      const userEmail = auth.currentUser?.email;

      let matchedClient: ClientProfile | null = null;
      const isAdminEmail = Boolean(
        userEmail && (userEmail.includes('voopetrans.com.br') || userEmail.includes('abdalaengenharia'))
      );

      if (userEmail) {
        // Tenta encontrar pessoa vinculada ao email do usuário logado
        const { data: pessoaVinculada } = await supabase
          .from('pessoa')
          .select('id, nome, documento, email, fone, system_user_id')
          .eq('email', userEmail)
          .maybeSingle();

        if (pessoaVinculada) {
          matchedClient = {
            id: pessoaVinculada.id,
            nome: (pessoaVinculada as any).nome_fantasia ? `${pessoaVinculada.nome} (${(pessoaVinculada as any).nome_fantasia})` : pessoaVinculada.nome,
            documento: pessoaVinculada.documento,
            email: pessoaVinculada.email,
            fone: pessoaVinculada.fone,
            nomeFantasia: (pessoaVinculada as any).nome_fantasia
          };
          setIsClientLocked(!isAdminEmail);
        }
      }

      // IMPORTANTE: só um e-mail interno/admin conhecido pode navegar entre todos os
      // clientes sem vínculo direto (uso de suporte). Qualquer outro usuário sem
      // vínculo específico NÃO recebe acesso a nenhum cliente — antes disso caía
      // silenciosamente no primeiro cliente da lista, expondo dados de terceiros.
      if (!matchedClient) {
        if (isAdminEmail && clientes.length > 0) {
          matchedClient = clientes[0];
          setIsClientLocked(false);
        } else {
          setSemVinculo(true);
          setIsClientLocked(true);
        }
      }

      setClienteAtual(matchedClient);

      if (matchedClient) {
        const statsData = await areaClienteService.getDashboardStats(matchedClient.id);
        setStats(statsData);
      }
    } catch (err) {
      console.error('Erro ao inicializar o Portal do Cliente:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    initializeClientPortal();
  }, [initializeClientPortal]);

  // Recarregar dados do cliente ativo
  const refreshData = async () => {
    if (!clienteAtual) return;
    try {
      const statsData = await areaClienteService.getDashboardStats(clienteAtual.id);
      setStats(statsData);
    } catch (err) {
      console.error('Erro ao atualizar estatísticas do cliente:', err);
    }
  };

  // Troca de cliente (apenas se não estiver bloqueado por conta individual)
  const selecionarClientePorId = async (id: number) => {
    const found = listaClientes.find(c => c.id === id);
    if (found) {
      setClienteAtual(found);
      setIsLoading(true);
      try {
        const statsData = await areaClienteService.getDashboardStats(found.id);
        setStats(statsData);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <AreaClienteContext.Provider
      value={{
        clienteAtual,
        setClienteAtual,
        listaClientes,
        stats,
        isLoading,
        isClientLocked,
        semVinculo,
        refreshData,
        selecionarClientePorId
      }}
    >
      {children}
    </AreaClienteContext.Provider>
  );
}

export function useAreaCliente() {
  const context = useContext(AreaClienteContext);
  if (!context) {
    throw new Error('useAreaCliente deve ser usado dentro de um AreaClienteProvider');
  }
  return context;
}
